import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: ${t.space2};
      vertical-align: middle;
      cursor: pointer;
      user-select: none;
    }
    :host([disabled]) {
      pointer-events: none;
      opacity: 0.6;
    }

    /* ── Track ──────────────────────────────────────────────────────────────── */
    .uwc-ts {
      /* Track size derives from the global font token so the switch rescales
         with the size/font presets. The base is the compact "xs" preset
         (×2 / ×1.1 ≈ 28×15px at the 0.875rem default); small/medium/large step
         up from here. A theme can still pin exact dimensions via
         --uwc-switch-width / --uwc-switch-height. */
      --_width:    var(--uwc-switch-width,    calc(var(--uwc-font-size-md, 0.875rem) * 2));
      --_height:   var(--uwc-switch-height,   calc(var(--uwc-font-size-md, 0.875rem) * 1.1));
      --_radius:   var(--uwc-switch-radius,   ${t.radiusFull});
      --_bg-off:   var(--uwc-switch-bg-off,   ${t.borderControl});
      --_bg-on:    var(--uwc-switch-bg-on,    var(--uwc-color-primary, ${t.primary}));
      --_color:    var(--uwc-switch-color,    var(--uwc-color-primary, ${t.primary}));
      /* Insets scale with the track height so the thumb stays proportional at
         every size (≈0.125rem gap at the default height). */
      --_pad:      calc(var(--_height) * 0.0833);
      --_dot-size: calc(var(--_height) - var(--_pad) * 2);

      position:       relative;
      display:        inline-flex;
      align-items:    center;
      width:          var(--_width);
      height:         var(--_height);
      flex-shrink:    0;
      border-radius:  var(--_radius);
      background:     var(--_bg-off);
      transition:     background-color ${t.durationBase};
    }

    /* Hidden input */
    input[type="checkbox"] {
      position: absolute;
      inset: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      cursor: pointer;
    }

    /* On state */
    :host([checked]) .uwc-ts { background: var(--_bg-on); }

    /* Focus ring */
    .uwc-ts:focus-within {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--_color) 25%, transparent);
    }

    /* ── Dot / thumb ────────────────────────────────────────────────────────── */
    .uwc-ts__dot {
      position:      absolute;
      left:          var(--_pad);
      width:         var(--_dot-size);
      height:        var(--_dot-size);
      border-radius: 50%;
      background:    ${t.colorWhite};
      box-shadow:    0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.12);
      transition:    transform ${t.durationBase} ${t.easingStandard};
      pointer-events: none;
    }
    :host([checked]) .uwc-ts__dot {
      transform: translateX(calc(var(--_width) - var(--_dot-size) - var(--_pad) * 2));
    }

    /* ── Invalid ────────────────────────────────────────────────────────────── */
    :host([invalid]) .uwc-ts {
      box-shadow: 0 0 0 2px var(--uwc-color-danger, ${t.danger});
    }

    /* ── Sizes ──────────────────────────────────────────────────────────────── */
    /* xs is the base (defined on .uwc-ts above); each step keeps the ~1.85:1
       track aspect ratio so the thumb stays proportional. */
    :host([size="small"]) .uwc-ts {
      --_width:  calc(var(--uwc-font-size-md, 0.875rem) * 2.43);
      --_height: calc(var(--uwc-font-size-md, 0.875rem) * 1.33);
    }
    :host([size="medium"]) .uwc-ts {
      --_width:  calc(var(--uwc-font-size-md, 0.875rem) * 2.86);
      --_height: calc(var(--uwc-font-size-md, 0.875rem) * 1.55);
    }
    :host([size="large"]) .uwc-ts {
      --_width:  calc(var(--uwc-font-size-md, 0.875rem) * 3.29);
      --_height: calc(var(--uwc-font-size-md, 0.875rem) * 1.78);
    }

    /* ── Label ──────────────────────────────────────────────────────────────── */
    .uwc-ts__label {
      font-size:   var(--uwc-switch-font-size, ${t.fontSizeMd});
      color:       var(--uwc-switch-label-color, ${t.text});
      line-height: 1.4;
    }
  `,
];
